// CatalogoFornecedor — pagina /catalogo-fornecedor.
//
// UM registro por (EMPRESA, codigo do fornecedor) — DA-310. MARTINS GO e MARTINS MG sao a MESMA
// empresa: dividem descricao, marca, NCM e produto, e cada uma pendura o SEU preco (a "perna").
// Nao existe mais "Fornecedor 2" (era a gambiarra pra amarrar a filial-irma) nem a visao "Por SKU"
// (o banco ja guarda um item por SKU — a lista normal JA E a visao por SKU).
//
// Quem compara filial com DIFAL hoje e o MONITOR DE CUSTOS, que mede os dois armazens a cada coleta
// e devolve o custo real pronto. Aqui a comparacao e simples e direta: o preco UNITARIO de cada
// filial, lado a lado.
import { type CSSProperties, useEffect, useMemo, useRef, useState } from 'react';
import { upperMantendoCursor } from '../../components/inputs/UpperInput';
import { useStickyHeader } from '../../lib/useStickyHeader';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ImageOff, Link2, Link2Off, Package, Plus, Save, Search, Trash2 } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useToast } from '../../components/Toast';
import { useDialog } from '../../components/DialogProvider';
import { MSG_PRODUTO_INATIVO, produtoInativo, SeloInativo } from '../../lib/produto-inativo';
import { ConfirmModal } from '../../components/ConfirmModal';
import { Modal } from '../../components/Modal';
import { ComboBox } from '../../components/ComboBox';
import { ModalCadastroRapido } from '../../components/ModalCadastroRapido';
import { useListaTeclado } from '../../lib/useListaTeclado';
import { FotoProduto } from '../../components/FotoProduto';
import { formatMoeda, formatMoedaSemSimbolo, parseMoeda, formatQtd, parseQtd, formatDataSP, formatHoraSP, getNomeFornecedor, limparCodigoFornecedor, pernaSemPrecoObrigatorio, pernaTemConteudo, contar } from '@fiodecobre/shared';

/** A UF de casa. Perna "no estado" = filial daqui; perna "de fora" = a que sofre DIFAL na entrada. */
const UF_CASA = 'GO';

/** A oferta de UMA filial. Preco, quantidade e embalagem sao DELA — a MARTINS GO fatura por METRO
 *  e a MG fatura o ROLO, e preco e quantidade andam juntos ou o unitario mente (DA-286). */
export interface Perna {
  id: number;
  filial_id: number;
  filial_apelido: string | null;
  fornecedor_nome: string | null;
  uf: string | null;
  embalagem: string | null;
  quantidade_embalagem: string | null;
  preco_unitario: string | null;      // o preco da EMBALAGEM (o nome da coluna e historico)
  preco_unit_real: string | null;     // preco / qtd — o numero que compara
  preco_origem?: 'nota' | 'monitor' | 'manual' | 'cotacao' | null;
  preco_atualizado_em?: string | null;
  monitor_oferta_id?: number | null;
  visto_em: string | null;
}

/** O item da EMPRESA. A identidade nao se repete por filial. */
export interface ItemCatalogo {
  id: number;
  fornecedor_id: number;
  empresa_nome: string | null;
  codigo_fornecedor: string | null;
  codigo_fornecedor_alt?: string | null;
  produto_id: number | null;
  produto_codigo_interno: string | null;
  produto_descricao: string | null;
  produto_ean?: string | null;
  // O hub da prateleira nova ([DA-610]). Ocupa o MESMO campo do produto interno na tela.
  novo_produto_id?: number | null;
  np_codigo?: string | null;
  np_descricao?: string | null;
  descricao_fornecedor: string | null;
  marca: string | null;
  ncm: string | null;
  codigo_barras: string | null;
  foto_url: string | null;
  origem_registro?: 'xml' | 'manual' | 'cotacao' | null;
  criado_por_nome?: string | null;
  // Contador de USO do codigo do fornecedor (pedido dele, 26/08/2026): quantas notas de entrada e
  // quantas respostas de cotacao ja citaram este codigo. Serve pra escolher qual linha apagar
  // quando o mesmo item ganhou dois codigos no mesmo fornecedor.
  // ⛔ VENDA nao entra: ela nao guarda codigo de fornecedor, entao o numero seria o do PRODUTO e
  // sairia igual nas duas linhas duplicadas — nao distinguiria justamente o que se esta comparando.
  importacoes?: number;
  cotacoes?: number;
  pernas: Perna[];
  perna_estado: Perna | null;
  perna_fora: Perna | null;
}

export interface FilialMin {
  id: number;
  apelido: string | null;
  cpf_cnpj: string | null;
  cidade: string | null;
  uf: string | null;
  principal?: boolean;
}

export interface FornecedorMin {
  id: number;
  nome_fantasia: string | null;
  /** As inscricoes da empresa (DA-311). Uma empresa tem sempre ao menos uma. */
  filiais?: FilialMin[];
}

const PRECO_ORIGEM_SELO: Record<string, { texto: string; titulo: string }> = {
  nota: { texto: 'nota', titulo: 'Preco da ultima nota de compra — o que pagamos de fato' },
  monitor: { texto: 'monitor', titulo: 'Preco coletado pelo robo no site do fornecedor (vitrine) — atualiza sozinho' },
  manual: { texto: 'digitado', titulo: 'Preco digitado a mao' },
  cotacao: { texto: 'cotacao', titulo: 'Preco que veio de uma cotacao' },
};

// Rotulo da coluna Cadastro: nome de quem cadastrou quando ha gente por tras; SISTEMA quando o item
// nasceu sozinho da nota de compra; "—" quando nao da pra saber (registro antigo). Nao inventa autor.
function cadastradoPor(it: ItemCatalogo): { texto: string; sistema: boolean; titulo: string } {
  if (it.criado_por_nome) {
    return {
      texto: it.criado_por_nome,
      sistema: false,
      titulo: it.origem_registro === 'cotacao'
        ? `Cadastrado por ${it.criado_por_nome} ao importar uma cotacao`
        : `Cadastrado a mao por ${it.criado_por_nome}`,
    };
  }
  if (it.origem_registro === 'xml') return { texto: 'SISTEMA', sistema: true, titulo: 'Criado pela importacao do XML da nota de compra' };
  if (it.origem_registro === 'cotacao') return { texto: 'SISTEMA', sistema: true, titulo: 'Veio de uma cotacao' };
  return { texto: '—', sistema: true, titulo: 'Registro antigo — a origem nao foi guardada' };
}

interface ProdutoBusca {
  id: number;
  codigo_interno: string;
  descricao: string;
  foto_url: string | null;
  marca_nome: string | null;
  /**
   * A linha veio da prateleira NOVOS PRODUTOS, nao do cadastro ([DA-610]). O campo "Produto
   * interno" busca nas DUAS e devolve o mesmo formato — pro comprador e' um item so, e a diferenca
   * aparece como selo, nao como outro campo. Regra do Ruan, 24/08/2026: *"Quando digitar na
   * pesquisa NP..., o sistema ja sabe que e' um produto de la"*.
   */
  novo_produto?: boolean;
  status?: string | null;
}

export function CatalogoFornecedor() {
  const navigate = useNavigate();
  const toast = useToast();

  const [fornecedores, setFornecedores] = useState<FornecedorMin[]>([]);
  const [busca, setBusca] = useState('');
  const [itens, setItens] = useState<ItemCatalogo[]>([]);
  const [loading, setLoading] = useState(false);
  const [editando, setEditando] = useState<ItemCatalogo | null>(null);
  const [criandoNovo, setCriandoNovo] = useState(false);
  const [confirmarDeletar, setConfirmarDeletar] = useState<ItemCatalogo | null>(null);
  // Vinculo com o Monitor e da PERNA (o robo consulta o codigo naquela filial), nao do item.
  const [vinculando, setVinculando] = useState<{ item: ItemCatalogo; perna: Perna } | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  // Faxina de vinculo: mostra SO os itens que ainda nao tem produto interno. Nasce desmarcado e
  // persiste (convencao da casa pra filtro de tela) — marcado, o proprio checkbox no topo e o
  // aviso de que a lista esta parcial, que e o que impede a preferencia guardada de enganar depois.
  const [semProduto, setSemProduto] = useState(() => localStorage.getItem('cat:sem-produto') === 'true');
  useEffect(() => { localStorage.setItem('cat:sem-produto', String(semProduto)); }, [semProduto]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const buscaInputRef = useRef<HTMLInputElement | null>(null);
  const sticky = useStickyHeader(120);

  useEffect(() => {
    api.get<FornecedorMin[]>('/api/fornecedores', { query: { todos: true } })
      .then(setFornecedores)
      .catch(() => setFornecedores([]));
  }, []);

  useEffect(() => { buscaInputRef.current?.focus(); }, []);

  // Deep-link ?fornecedor=<id>&codigo=<cod> — o lapis do item na Notas de Entrada cai aqui ja com
  // a janela de edicao aberta. Quem acha a linha e' o /por-codigo, a mesma regra de casamento da
  // importacao (codigo, codigo alternativo, normalizacao do DA-471); a lista so empresta o item no
  // formato completo (com as pernas) que a janela precisa.
  const [searchParams] = useSearchParams();
  const deepLinkFeito = useRef(false);
  // A janela aberta pelo lapis da nota fecha VOLTANDO pra nota (relato dele, 29/10/2026: fechou e
  // ficou na lista do Catalogo). So vale pra primeira janela; as abertas depois na lista fecham normal.
  const abertoPeloDeepLink = useRef(false);
  const voltarSeVeioDeFora = () => {
    if (!abertoPeloDeepLink.current) return;
    abertoPeloDeepLink.current = false;
    if (window.history.length > 1) navigate(-1);
  };
  useEffect(() => {
    const fornecedorId = Number(searchParams.get('fornecedor'));
    const codigo = (searchParams.get('codigo') || '').trim();
    if (!fornecedorId || !codigo || deepLinkFeito.current) return;
    deepLinkFeito.current = true;
    setBusca(codigo.toUpperCase());
    (async () => {
      try {
        const r = await api.get<{ encontrado: boolean; item: { item_id: number } | null }>(
          '/api/fornecedor-catalogo/por-codigo', { query: { fornecedor_id: fornecedorId, codigo } },
        );
        if (!r.encontrado || !r.item) {
          toast.erro(`O codigo ${codigo} nao tem linha no catalogo deste fornecedor.`);
          return;
        }
        const lista = await api.get<ItemCatalogo[]>('/api/fornecedor-catalogo/itens', {
          query: { fornecedor_id: fornecedorId, q: codigo.toUpperCase() },
        });
        const achado = (Array.isArray(lista) ? lista : []).find((x) => x.id === r.item!.item_id);
        if (achado) { abertoPeloDeepLink.current = true; setEditando(achado); }
        else toast.erro(`Nao consegui abrir o item ${codigo} do catalogo.`);
      } catch (e: unknown) {
        toast.erro(e instanceof ApiError ? e.message : 'Falha ao abrir o item do catalogo.');
      }
    })();
  }, [searchParams, toast]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get<ItemCatalogo[]>('/api/fornecedor-catalogo/itens', {
          query: { ...(busca ? { q: busca } : {}), ...(semProduto ? { sem_produto: '1' } : {}) },
        });
        setItens(Array.isArray(r) ? r : []);
      } catch {
        setItens([]);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [busca, semProduto, reloadKey]);

  async function deletar(it: ItemCatalogo) {
    try {
      await api.delete(`/api/fornecedor-catalogo/${it.id}`);
      toast.sucesso('Item removido do catalogo.');
      setConfirmarDeletar(null);
      setReloadKey((k) => k + 1);
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao remover');
    }
  }

  // thStyle vem do hook (sticky + cores do tema). O container da tabela NAO pode ter overflow: um
  // ancestral com overflow cria contexto de scroll proprio e o cabecalho sticky, em vez de grudar
  // no topo da pagina, desce e cobre a primeira linha.
  const thBase: CSSProperties = { ...sticky.thStyle, fontWeight: 600, padding: '8px 12px' };

  return (
    <div style={{ padding: 16, maxWidth: '100%' }}>
      <div ref={sticky.headerRef} style={{ position: 'sticky', top: 0, zIndex: 20, background: 'var(--page-bg)', paddingBottom: 8 }}>
        <div className="pagina-header">
          <h2 className="pagina-titulo" style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="btn-icon"
              title="Voltar"
              aria-label="Voltar"
              style={{ marginRight: 4 }}
            >
              <ArrowLeft size={20} />
            </button>
            Catalogo
          </h2>
          <div className="pagina-acoes">
            <button type="button" onClick={() => setCriandoNovo(true)} className="btn-primary"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }} title="Cadastrar novo item no catalogo">
              <Plus size={16} /> Novo Item
            </button>
          </div>
        </div>

        <div style={{ position: 'relative', marginBottom: 8 }}>
          <Search size={18} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)' }} />
          <input
            ref={buscaInputRef}
            type="text"
            value={busca}
            onChange={(e) => setBusca(upperMantendoCursor(e))}
            placeholder="BUSCAR POR FORNECEDOR, COD. INTERNO, COD. FORNEC., DESCRICAO, MARCA OU NCM..."
            style={{
              width: '100%', padding: '10px 12px 10px 40px', border: '1px solid var(--border)',
              borderRadius: 8, fontSize: 13, background: 'var(--input-bg)', color: 'var(--text-color)',
              textTransform: 'uppercase',
            }}
          />
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, fontSize: 11, color: 'var(--text-muted)' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 12 }}
            title="Mostra so os itens que ainda nao tem produto interno vinculado">
            <input type="checkbox" checked={semProduto} onChange={(e) => setSemProduto(e.target.checked)} />
            Exibir sem Codigo Interno
          </label>
          <span>
            {loading ? 'Carregando...' : `${itens.length} ${itens.length === 1 ? 'item' : 'itens'}`}
            {/* Com o filtro ligado a lista e PARCIAL — dizer isso aqui evita ler "12 itens" como
                "o catalogo tem 12". */}
            {!loading && semProduto && <span style={{ color: '#c2410c', fontWeight: 600 }}> sem codigo interno</span>}
          </span>
        </div>
      </div>

      {!loading && itens.length === 0 && (
        <div style={{
          background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8,
          padding: '48px 16px', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13,
        }}>
          <Package size={32} style={{ margin: '0 auto 8px', display: 'block', color: 'var(--border)' }} />
          {busca
            ? <>Nenhum item encontrado pra <span style={{ fontFamily: 'monospace' }}>{busca}</span>.</>
            : <>Sem itens no catalogo. Use <strong>Novo Item</strong> pra cadastrar.</>}
        </div>
      )}

      {itens.length > 0 && (
        <div style={{ background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 8 }}>
          <table style={{ ...sticky.tableStyle, width: '100%', fontSize: 12 }}>
            <thead>
              <tr style={{ background: 'var(--grid-header-bg, #f3f4f6)', color: 'var(--grid-header-fg, #374151)' }}>
                <th style={{ ...thBase, padding: 8, textAlign: 'center', width: 56 }}>Foto</th>
                <th style={{ ...thBase, textAlign: 'left', width: 155 }}>Fornecedor</th>
                <th
                  style={{ ...thBase, textAlign: 'left', width: 96 }}
                  title="Quantas notas de entrada e quantas respostas de cotacao ja citaram ESTE codigo do fornecedor. Venda nao entra: ela nao guarda codigo de fornecedor."
                >
                  Uso
                </th>
                <th style={{ ...thBase, textAlign: 'left', width: 80 }}>Cod. Interno</th>
                {/* As duas UNICAS colunas elasticas da tabela (as outras tem largura fixa) — e, com a
                    soma das larguras passando da tela, sao as unicas que o navegador NAO espreme:
                    ficam presas neste minimo. Entao o numero daqui e' a largura que aparece.
                    Rebalanceadas a pedido dele em 05/09/2026: -15% na primeira (200 -> 170) e +15%
                    na segunda (190 -> 219), porque a descricao do fornecedor e' a mais longa e
                    quebrava em cinco linhas enquanto a do produto interno sobrava. */}
                <th style={{ ...thBase, textAlign: 'left', minWidth: 170 }}>Produto Interno</th>
                <th style={{ ...thBase, textAlign: 'left', width: 96 }}>Cod. Forn.</th>
                <th style={{ ...thBase, textAlign: 'left', minWidth: 252 }}>Descricao Fornecedor</th>
                {/* ⚠️ Esta e' de largura FIXA e a tabela esta com a soma passando da tela, entao ela
                    desenha MENOS do que declara (100 -> 86px). O numero daqui e' a fatia dela na
                    divisao, nao a largura final. Rebalanceada com a Descricao a pedido dele. */}
                <th style={{ ...thBase, textAlign: 'left', width: 85 }}>Marca</th>
                {/* Uma coluna so pros precos, com a UF de cada oferta na etiqueta. Antes eram duas
                    fixas (No Estado / Outros Estados) e a UF nao aparecia em lugar nenhum. */}
                <th
                  style={{ ...thBase, textAlign: 'left', minWidth: 190 }}
                  title={`O preco de cada filial deste fornecedor, com a UF ao lado. ${UF_CASA} e' a daqui; as de fora sofrem DIFAL na entrada. Quando ha duas, a mais barata por unidade vai em amarelo.`}
                >
                  Preco · UF
                </th>
                <th style={{ ...thBase, textAlign: 'left', width: 90 }}>NCM</th>
                <th style={{ ...thBase, textAlign: 'left', width: 96 }}>Cadastro</th>
                <th style={{ ...thBase, width: 36 }}></th>
              </tr>
            </thead>
            <tbody>
              {itens.map((it, idx) => {
                const cad = cadastradoPor(it);
                return (
                  <tr
                    key={it.id}
                    onClick={() => setEditando(it)}
                    style={{
                      borderBottom: '1px solid var(--border)',
                      cursor: 'pointer',
                      background: idx % 2 === 1 ? 'var(--grid-row-alt-bg, #fafafa)' : 'var(--grid-row-bg, #fff)',
                    }}
                    title="Editar item do catalogo"
                  >
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      {it.foto_url
                        ? <FotoProduto url={it.foto_url} tam={40} style={{ margin: '0 auto' }} />
                        : <ImageOff size={16} style={{ color: 'var(--border)' }} />}
                    </td>
                    <td style={{ padding: '8px 12px', fontWeight: 600 }}>{it.empresa_nome || '—'}</td>
                    {/* USO deste codigo. O estado que ele cacava e o ZERO — a linha que nunca foi
                        citada e a que da' pra apagar quando o mesmo item ganhou dois codigos. Por
                        isso o zero vai por EXTENSO e destacado, e nao como '0': um zero no meio de
                        numeros nao se le como "pode apagar".
                        ⚠️ Destaque em `#fde68a`+preto, nunca verde/vermelho — ele nao distingue os
                        dois. Numeros SEMPRE com a palavra do lado: '1 / 3' nao diz qual e qual. */}
                    <td style={{ padding: '8px 12px', fontSize: 11, lineHeight: 1.3 }}>
                      {(() => {
                        const imp = Number(it.importacoes ?? 0);
                        const cot = Number(it.cotacoes ?? 0);
                        if (imp === 0 && cot === 0) {
                          return (
                            <span
                              style={{ background: '#fde68a', color: '#000', borderRadius: 3, padding: '1px 6px', fontWeight: 700, whiteSpace: 'nowrap' }}
                              title="Nenhuma nota de entrada e nenhuma cotacao citaram este codigo. E a linha segura de apagar quando ha duplicata."
                            >
                              nunca usado
                            </span>
                          );
                        }
                        return (
                          <span title={`${imp} importacao(oes) e ${cot} cotacao(oes) com este codigo`}>
                            <span style={{ display: 'block', color: imp ? undefined : 'var(--text-muted)' }}>
                              {imp} {imp === 1 ? 'importacao' : 'importacoes'}
                            </span>
                            <span style={{ display: 'block', color: cot ? undefined : 'var(--text-muted)' }}>
                              {cot} {cot === 1 ? 'cotacao' : 'cotacoes'}
                            </span>
                          </span>
                        );
                      })()}
                    </td>
                    {/* Cod. Interno — o do cadastro, ou o da prateleira nova quando o item ainda
                        nao virou produto ([DA-610]). O selo lilas e' o mesmo do dropdown: sem ele,
                        `NP00008` na coluna do codigo interno leria como codigo de produto real. */}
                    <td style={{ padding: '8px 12px' }}>
                      {it.produto_codigo_interno
                        ? <span className="text-gray-500 font-bold no-underline">{it.produto_codigo_interno}</span>
                        : it.np_codigo
                          ? (
                            <span style={{ background: '#c4b5fd', color: '#000', borderRadius: 3, padding: '1px 6px', fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}
                                  title="Item da prateleira Novos Produtos — ainda nao virou produto do cadastro">
                              {it.np_codigo}
                            </span>
                          )
                          : <span className="text-gray-500 font-bold no-underline">—</span>}
                    </td>
                    {/* ⛔ "sem produto" so vale quando NAO ha hub nenhum. Com o item apontando pra
                        prateleira nova, aquele texto AFIRMAVA algo falso — a linha tem vinculo, e a
                        tela dizia que nao. */}
                    <td style={{ padding: '8px 12px' }}>
                      {it.produto_descricao
                        || it.np_descricao
                        || <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>sem produto</span>}
                    </td>
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>
                      {it.codigo_fornecedor || '—'}
                      {it.codigo_fornecedor_alt && (
                        <span style={{ display: 'block', fontSize: 10, color: 'var(--text-muted)' }}
                              title="Segundo codigo do mesmo item no fornecedor (o da nota)">
                          alt: {it.codigo_fornecedor_alt}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '8px 12px' }}>{it.descricao_fornecedor || '—'}</td>
                    <td style={{ padding: '8px 12px' }}>{it.marca || '—'}</td>
                    <CelulaPrecos
                      item={it}
                      onVincular={(p) => setVinculando({ item: it, perna: p })}
                    />
                    <td style={{ padding: '8px 12px', fontFamily: 'monospace' }}>{it.ncm || '—'}</td>
                    <td style={{ padding: '8px 12px', color: cad.sistema ? 'var(--text-muted)' : undefined }} title={cad.titulo}>
                      {cad.texto}
                    </td>
                    <td style={{ padding: 4, textAlign: 'center' }}>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setConfirmarDeletar(it); }}
                        className="btn-icone"
                        title="Remover o item (e as ofertas de todas as filiais)"
                        style={{ color: 'var(--text-muted)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(editando || criandoNovo) && (
        <ModalEditarCatalogoItem
          item={editando}
          fornecedores={fornecedores}
          onFechar={() => { setEditando(null); setCriandoNovo(false); voltarSeVeioDeFora(); }}
          onSalvo={() => { setEditando(null); setCriandoNovo(false); setReloadKey((k) => k + 1); voltarSeVeioDeFora(); }}
        />
      )}

      {vinculando && (
        <ModalVincularMonitor
          item={vinculando.item}
          perna={vinculando.perna}
          onClose={() => setVinculando(null)}
          onSaved={() => { setVinculando(null); setReloadKey((k) => k + 1); }}
        />
      )}

      {confirmarDeletar && (
        <ConfirmModal
          aberto
          titulo="Remover item do catalogo"
          mensagem={`Remover "${confirmarDeletar.descricao_fornecedor || confirmarDeletar.codigo_fornecedor}" do catalogo? As ofertas de TODAS as filiais desta empresa saem junto.`}
          textoConfirmar="Remover"
          perigoso
          onConfirmar={() => void deletar(confirmarDeletar)}
          onCancelar={() => setConfirmarDeletar(null)}
        />
      )}
    </div>
  );
}

/** A perna mais barata pelo UNITARIO (nao pelo preco da embalagem — a MARTINS GO vende o metro e a
 *  MG vende o rolo; comparar o preco da caixa mentiria). Sem as duas com preco, ninguem ganha selo. */
function ehMaisBarata(a: Perna | null, b: Perna | null): boolean {
  const ua = a?.preco_unit_real != null ? Number(a.preco_unit_real) : null;
  const ub = b?.preco_unit_real != null ? Number(b.preco_unit_real) : null;
  if (ua == null || ub == null) return false;
  return ua < ub;
}

/**
 * A coluna de PRECO do catalogo — UMA so, com a UF de cada oferta na etiqueta (05/09/2026).
 *
 * Ate aqui eram duas colunas fixas — `No Estado` e `Outros Estados` —, e a UF nao aparecia em
 * lugar nenhum: ela so decidia em QUAL das duas o preco caia. Media antes de mexer: dos 730 itens,
 * **622 tem uma filial so**, entao em 85% das linhas uma das duas colunas estava vazia; e as
 * ofertas de fora vem de seis estados diferentes (MG 177, SC 35, PR 25, SP 9, RS 4 e 10 sem UF)
 * que a tela empilhava como se fossem a mesma coisa.
 *
 * ⚠️ O destaque da mais barata NAO se perde ao juntar: com duas ofertas na mesma celula, a mais
 * barata por unidade leva o fundo amarelo — e ela e' a razao de a coluna existir. Amarelo + preto
 * por daltonismo: verde/vermelho nao servem nesta casa.
 *
 * ⚠️ Nunca ha mais de duas ofertas por item (medido: 622 com uma, 108 com duas, nenhuma com tres),
 * entao empilhar nao gera linha sem fim. As 108 de duas ofertas ficam mais altas que antes, que e'
 * o preco pago — e foi aceito por ele vendo o esboco com dado real.
 */
function CelulaPrecos({ item, onVincular }: {
  item: ItemCatalogo;
  onVincular: (p: Perna) => void;
}) {
  const ofertas = [item.perna_estado, item.perna_fora].filter(Boolean) as Perna[];
  if (!ofertas.length) {
    return (
      <td style={{ padding: '8px 12px', textAlign: 'right', color: 'var(--text-muted)' }}
          title="Nenhuma filial deste fornecedor tem oferta deste item">
        —
      </td>
    );
  }
  return (
    <td style={{ padding: '4px 6px' }}>
      {ofertas.map((perna) => {
        const outra = ofertas.find((o) => o !== perna) ?? null;
        const melhor = ofertas.length > 1 && ehMaisBarata(perna, outra);
        const selo = perna.preco_origem ? PRECO_ORIGEM_SELO[perna.preco_origem] : null;
        const temPreco = perna.preco_unitario != null && Number(perna.preco_unitario) > 0;
        const qtd = perna.quantidade_embalagem != null ? Number(perna.quantidade_embalagem) : null;
        return (
          <div
            key={perna.id}
            style={{
              display: 'flex', alignItems: 'flex-start', gap: 5,
              padding: '2px 3px', borderRadius: 4,
              // Destaque daltonismo-safe: amarelo + preto (verde/vermelho nao servem).
              background: melhor ? '#fde68a' : undefined,
              color: melhor ? '#000' : undefined,
            }}
            title={melhor ? 'A filial mais barata por unidade' : undefined}
          >
            {/* ⛔ Cores FIXAS, nunca as variaveis do tema. A etiqueta nasceu com
                `var(--grid-header-bg)` de fundo e `var(--text-muted)` de fonte, e no tema dele isso
                deu #5C6B8A sobre #11224A — contraste 2,7:1, em corpo 9px: ilegivel, e ele achou no
                primeiro uso. O par amarelo+preto e' o destaque daltonismo-safe da casa, e ele so
                funciona se o fundo for escuro nos DOIS temas — daqui a fonte clara nao pode
                depender de qual tema esta ligado. */}
            <span
              title={perna.filial_apelido || `Filial de ${perna.uf || 'UF nao informada'}`}
              style={{
                flex: '0 0 auto', marginTop: 2, borderRadius: 3, padding: '1px 4px',
                fontSize: 9, fontWeight: 700, fontFamily: 'ui-monospace, monospace',
                background: melhor ? '#000' : '#11224a',
                color: '#fde68a',
              }}
            >
              {perna.uf || '--'}
            </span>
            <span style={{ flex: 1, minWidth: 0, textAlign: 'right' }}>
              {!temPreco ? (
                <span style={{ color: melhor ? '#000' : 'var(--text-muted)' }}>sem preco</span>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4 }}>
                    <span style={{ fontWeight: 600 }}>{formatMoeda(Number(perna.preco_unitario))}</span>
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); onVincular(perna); }}
                      className="btn-icone"
                      title={perna.monitor_oferta_id
                        ? 'Alimentado pelo Monitor de Custos — o preco se atualiza sozinho a cada coleta'
                        : 'Vincular ao Monitor de Custos'}
                      style={{ color: perna.monitor_oferta_id ? '#0ea5e9' : 'var(--text-muted)', padding: 2 }}
                    >
                      {perna.monitor_oferta_id ? <Link2 size={12} /> : <Link2Off size={12} />}
                    </button>
                  </div>
                  {/* ⚠️ NAO e' `--text-muted` puro: medido no tema dele, aquela cor da 3,9:1 sobre a
                      linha par e 3,5:1 sobre a impar — abaixo do minimo legivel, em corpo 10. A
                      mistura com `--text-color` sobe pra 6,3/5,6 e continua visivelmente
                      secundaria (a cor cheia daria 12,3). Montada com as DUAS cores do proprio
                      tema, entao acompanha a troca de tema em vez de brigar com ela; navegador que
                      nao entenda `color-mix` simplesmente herda a cor do texto, que e' mais escura
                      ainda — degrada pro lado seguro.
                      ⛔ `--text-muted` esta fraco assim no APP INTEIRO neste tema; aqui so o selo
                      foi corrigido, que foi o que ele aprovou. O resto e' decisao dele. */}
                  <div style={{ fontSize: 10, color: melhor ? '#000' : 'color-mix(in srgb, var(--text-color) 40%, var(--text-muted))' }}>
                    {qtd && qtd !== 1 ? `${perna.embalagem || 'emb.'} c/ ${formatQtd(qtd)} · ` : ''}
                    {perna.preco_unit_real != null && (
                      <strong>{formatMoedaSemSimbolo(Number(perna.preco_unit_real), 4)}/un</strong>
                    )}
                    {selo && <span title={selo.titulo}> · {selo.texto}</span>}
                  </div>
                </>
              )}
            </span>
          </div>
        );
      })}
    </td>
  );
}

// ============================================================
// Vinculo com o Monitor de Custos — e da PERNA: o robo consulta o codigo NAQUELA filial.
// Vinculada, cada coleta derruba preco + qtd da embalagem aqui sozinho (DA-309). E como o robo ja
// mede os DOIS armazens, a coleta preenche as duas pernas de uma vez (DA-310).
// ============================================================

interface CandidatoMonitor {
  id: number;
  codigo_fornecedor: string;
  uf: string;
  metragem_embalagem: string;
  familia_nome: string;
  ja_vinculada_em: number | null;
}

function ModalVincularMonitor({ item, perna, onClose, onSaved }: {
  item: ItemCatalogo;
  perna: Perna;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [temColetor, setTemColetor] = useState(true);
  const [motivo, setMotivo] = useState('');
  const [candidato, setCandidato] = useState<CandidatoMonitor | null>(null);
  const [familias, setFamilias] = useState<Array<{ id: number; nome: string }>>([]);
  const [familiaId, setFamiliaId] = useState<number | ''>('');

  const vinculado = !!perna.monitor_oferta_id;

  useEffect(() => {
    let vivo = true;
    api.get<any>(`/api/fornecedor-catalogo/${perna.id}/monitor-candidato`)
      .then((r) => {
        if (!vivo) return;
        setTemColetor(!!r.tem_coletor);
        setMotivo(r.motivo || '');
        setCandidato(r.candidato ?? null);
        setFamilias(r.familias ?? []);
      })
      .catch((e: unknown) => toast.erro(e instanceof ApiError ? e.message : 'Erro ao consultar o Monitor'))
      .finally(() => { if (vivo) setCarregando(false); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perna.id]);

  async function vincular(body: { monitor_oferta_id?: number; familia_id?: number }) {
    try {
      setSalvando(true);
      await api.post(`/api/fornecedor-catalogo/${perna.id}/vincular-monitor`, body);
      toast.sucesso('Vinculado ao Monitor — o preco passa a se atualizar sozinho a cada coleta.');
      onSaved();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao vincular');
    } finally { setSalvando(false); }
  }

  async function desvincular() {
    try {
      setSalvando(true);
      await api.delete(`/api/fornecedor-catalogo/${perna.id}/vincular-monitor`);
      toast.sucesso('Desvinculado. O preco atual fica como esta — so para de se atualizar sozinho.');
      onSaved();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao desvincular');
    } finally { setSalvando(false); }
  }

  const caixa: CSSProperties = {
    background: 'var(--card-bg)', border: '1px solid var(--panel-border, #e5e7eb)',
    borderRadius: 6, padding: 10, fontSize: 13, marginBottom: 12,
  };
  const alerta: CSSProperties = { background: '#f97316', color: '#000', padding: 10, borderRadius: 6, fontSize: 13, fontWeight: 500 };

  return (
    <Modal
      aberto
      onFechar={onClose}
      titulo="Vincular ao Monitor de Custos"
      largura={560}
      rodape={<button type="button" className="btn-secundario" onClick={onClose} disabled={salvando}>Fechar</button>}
    >
      <p style={{ marginTop: 0, fontSize: 13, color: 'var(--text-muted)' }}>
        <strong style={{ color: 'var(--page-fg)' }}>{perna.fornecedor_nome}</strong><br />
        {item.codigo_fornecedor} — {item.descricao_fornecedor || item.produto_descricao || 'sem descricao'}
      </p>

      {carregando && <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-muted)' }}>Consultando o Monitor...</div>}

      {!carregando && !temColetor && <div style={alerta}>{motivo}</div>}

      {!carregando && temColetor && vinculado && (
        <>
          <div style={caixa}>
            Esta oferta ja e alimentada pelo Monitor. A cada coleta do robo, o preco e a quantidade
            da embalagem sao atualizados aqui sozinhos — e como o robo mede os dois armazens da
            Martins, a filial de fora e atualizada junto.
          </div>
          <button type="button" className="btn-secundario" onClick={() => void desvincular()} disabled={salvando}>
            Desvincular do Monitor
          </button>
        </>
      )}

      {!carregando && temColetor && !vinculado && candidato && (
        <>
          <div style={caixa}>
            O Monitor ja tem o codigo <strong>{candidato.codigo_fornecedor}</strong> na familia{' '}
            <strong>{candidato.familia_nome}</strong> ({candidato.uf} · emb. {Number(candidato.metragem_embalagem).toLocaleString('pt-BR')}).
          </div>
          {candidato.ja_vinculada_em ? (
            <div style={alerta}>
              Essa linha do Monitor ja alimenta outro item do catalogo. Uma linha do Monitor so pode alimentar um item.
            </div>
          ) : (
            <button type="button" className="btn-primario" onClick={() => void vincular({ monitor_oferta_id: candidato.id })} disabled={salvando}>
              {salvando ? 'Vinculando...' : 'Vincular a essa linha'}
            </button>
          )}
        </>
      )}

      {!carregando && temColetor && !vinculado && !candidato && (
        <>
          <div style={caixa}>
            O Monitor ainda nao acompanha esse codigo. Escolha a familia onde ele deve entrar — o
            codigo e cadastrado la e passa a ser coletado junto com os outros.
          </div>
          <label style={{ display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>Familia do Monitor</label>
          <select
            value={familiaId}
            onChange={(e) => setFamiliaId(e.target.value ? Number(e.target.value) : '')}
            style={{ width: '100%', padding: '8px 10px', borderRadius: 6, background: 'var(--input-bg)', color: 'var(--text-color)', marginBottom: 12 }}
          >
            <option value="">Escolha a familia...</option>
            {familias.map((f) => <option key={f.id} value={f.id}>{f.nome}</option>)}
          </select>
          <button
            type="button"
            className="btn-primario"
            onClick={() => familiaId && void vincular({ familia_id: Number(familiaId) })}
            disabled={salvando || !familiaId}
          >
            {salvando ? 'Vinculando...' : 'Cadastrar no Monitor e vincular'}
          </button>
        </>
      )}
    </Modal>
  );
}

// ============================================================
// Modal do item — a identidade e da EMPRESA (uma vez); o preco e de cada FILIAL (uma perna cada).
// Cadastrar uma vez ja contempla as duas filiais: o passo de criar a "linha-espelho" adiantada
// (o velho Fornecedor 2) saiu do fluxo.
// ============================================================

interface PernaForm {
  filial_id: number;
  filial_nome: string;
  uf: string | null;
  perna_id: number | null;
  embalagem: string;
  quantidade_embalagem: string;
  preco_unitario: string;
  // So-leitura: quando o preco desta perna foi atualizado pela ultima vez, e por qual via.
  // Vazio quando o preco e anterior ao rastreio (coluna nova) ou a perna ainda nao tem preco.
  preco_atualizado_em: string | null;
  preco_origem: string | null;
}

interface ModalProps {
  item: ItemCatalogo | null;
  /** Pre-preenche um item NOVO (usado pelo Monitor: "vincular este codigo ao catalogo"). */
  prefill?: {
    fornecedor_id?: number;
    codigo_fornecedor?: string | null;
    descricao_fornecedor?: string | null;
    marca?: string | null;
    quantidade_embalagem?: string | number | null;
  } | null;
  /** Opcional: quem ja tem a lista passa pra evitar um request. Sem ela, o modal busca sozinho —
   *  ele PRECISA dos fornecedores pra saber quais filiais compoem a empresa. */
  fornecedores?: FornecedorMin[];
  onFechar: () => void;
  onSalvo: () => void;
}

/** O item que JA existe com o codigo digitado, devolvido pelo `GET /por-codigo` ([DA-635]). */
type ItemMesmoCodigo = {
  item_id: number;
  codigo_fornecedor: string | null;
  produto_id: number | null;
  descricao_fornecedor: string | null;
  produto_codigo: string | null;
  produto_descricao: string | null;
};

const CHAVE_ULTIMO_FORNECEDOR = 'cat:ultimo-fornecedor';

export function ModalEditarCatalogoItem({ item, prefill, fornecedores: fornecedoresProp, onFechar, onSalvo }: ModalProps) {
  const toast = useToast();
  const dialog = useDialog();

  const [fornecedoresLocais, setFornecedoresLocais] = useState<FornecedorMin[]>([]);
  const fornecedores = fornecedoresProp ?? fornecedoresLocais;
  useEffect(() => {
    if (fornecedoresProp) return;
    api.get<FornecedorMin[]>('/api/fornecedores', { query: { todos: true } })
      .then(setFornecedoresLocais)
      .catch(() => setFornecedoresLocais([]));
  }, [fornecedoresProp]);

  const [produtoId, setProdutoId] = useState<number | null>(item?.produto_id ?? null);
  // O MESMO campo guarda os dois hubs, um de cada vez ([DA-610]) — escolher um limpa o outro, como
  // no banco, onde produto e prateleira nova sao alternativas e nao acumulam.
  const [novoProdutoId, setNovoProdutoId] = useState<number | null>(item?.novo_produto_id ?? null);
  const [produtoBuscaText, setProdutoBuscaText] = useState(
    item?.produto_codigo_interno && item?.produto_descricao
      ? `${item.produto_codigo_interno} — ${item.produto_descricao}`
      // Item vinculado a prateleira nova abre mostrando o NP. Sem isto o campo abriria VAZIO num
      // item que TEM vinculo, e salvar apagaria o dado sem ninguem pedir (DA-607 p.2).
      : (item?.np_codigo ? `${item.np_codigo} — ${item.np_descricao ?? ''}` : ''),
  );
  const [trocandoProduto, setTrocandoProduto] = useState(false);
  // Foto do produto interno vinculado — so existe quando ha produto (codigo interno).
  const [fotoUrl, setFotoUrl] = useState<string | null>(item?.foto_url ?? null);
  const produtoFixo = !!(item?.produto_id || item?.novo_produto_id) && !trocandoProduto;
  const [resultadosProduto, setResultadosProduto] = useState<ProdutoBusca[]>([]);
  const buscaTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [codigoFornecedor, setCodigoFornecedor] = useState(item?.codigo_fornecedor || prefill?.codigo_fornecedor || '');
  // O SEGUNDO codigo do mesmo item ([DA-307]): o principal e o que a gente usa pra comprar e cotar,
  // o alternativo e o que o fornecedor manda na NF-e. Existe no banco desde 14/07/2026 e ate a
  // [DA-598] nao tinha onde ser digitado — a importacao do XML criava uma linha nova toda vez.
  const [codigoAlt, setCodigoAlt] = useState(item?.codigo_fornecedor_alt || '');
  const [descricaoFornecedor, setDescricaoFornecedor] = useState(item?.descricao_fornecedor || prefill?.descricao_fornecedor || '');
  const [marca, setMarca] = useState(item?.marca || prefill?.marca || '');
  const [ncm, setNcm] = useState(item?.ncm || '');

  const [salvando, setSalvando] = useState(false);
  const [marcas, setMarcas] = useState<{ id: number; nome: string }[]>([]);
  const [mostrarNovaMarca, setMostrarNovaMarca] = useState(false);
  const [unidades, setUnidades] = useState<{ id: number; sigla: string; nome: string; ativo?: boolean }[]>([]);

  // A EMPRESA dona do item. Num item existente ela ja esta la; num novo, o usuario escolhe.
  // Item novo sem prefill abre com o fornecedor do ULTIMO cadastro (pedido dele, [DA-826]). Le a
  // string antes de converter: Number(null) e' 0 ([DA-659] p.6).
  const [empresaId, setEmpresaId] = useState<number | ''>(() => {
    if (item) return item.fornecedor_id;
    if (prefill?.fornecedor_id != null) return prefill.fornecedor_id;
    if (prefill) return '';
    try {
      const s = localStorage.getItem(CHAVE_ULTIMO_FORNECEDOR);
      return s && /^\d+$/.test(s) ? Number(s) : '';
    } catch { return ''; }
  });
  // O lembrado pode ter saido do cadastro: sem ele na lista o combo abriria vazio com um id por baixo.
  useEffect(() => {
    if (item || empresaId === '' || fornecedores.length === 0) return;
    if (!fornecedores.some((f) => f.id === Number(empresaId))) setEmpresaId('');
  }, [fornecedores, empresaId, item]);

  // Item existente so troca de fornecedor sem importacao de XML (pedido dele, 19/10/2026 —
  // cotacao e Monitor NAO travam). Libera so com o contador CONHECIDO e zerado: quem abre o modal
  // sem ele (o Monitor) fica travado. A regra de verdade e' a do servidor (`importacoesDoItemCatalogo`).
  const vinculadoAoMonitor = !!item?.pernas?.some((p) => p.monitor_oferta_id);
  const podeTrocarFornecedor = !item || item.importacoes === 0;
  const trocandoFornecedor = !!item && empresaId !== '' && Number(empresaId) !== item.fornecedor_id;

  // As filiais (inscricoes) da empresa vem do CADASTRO desde o [DA-311] — antes a tela tinha que
  // adivinhar quais fornecedores eram "a mesma empresa" comparando a raiz do CNPJ na mao.
  const filiais = useMemo<FilialMin[]>(() => {
    if (!empresaId) return [];
    const emp = fornecedores.find((f) => f.id === Number(empresaId));
    const lista = emp?.filiais ?? [];
    return [...lista].sort((a, b) => {
      const ua = String(a.uf || '').toUpperCase() === UF_CASA ? 0 : 1;
      const ub = String(b.uf || '').toUpperCase() === UF_CASA ? 0 : 1;
      return ua - ub || String(a.apelido || '').localeCompare(String(b.apelido || ''));
    });
  }, [empresaId, fornecedores]);

  // Uma linha de preco por inscricao da empresa. As que ja tem oferta vem preenchidas; as que nao
  // tem aparecem em branco, prontas pra receber — e uma sem preco simplesmente nao vira perna.
  const [pernas, setPernas] = useState<PernaForm[]>([]);
  useEffect(() => {
    // Trocando de fornecedor, as inscricoes sao OUTRAS e nenhuma perna antiga casa por filial. O
    // preco que ele digitou era do item certo no fornecedor errado: vai pra inscricao principal da
    // empresa nova (a do estado, quando a antiga tinha as duas), senao sumiria calado.
    const origemTroca = item && empresaId !== '' && Number(empresaId) !== item.fornecedor_id
      ? (item.perna_estado ?? item.pernas?.[0] ?? null)
      : null;
    const principalNova = origemTroca
      ? (filiais.find((f) => f.principal) ?? filiais[0] ?? null)
      : null;
    // Item NOVO abre a inscricao principal com UN/1 (pedido dele, [DA-826]). So a principal: o
    // preco e' obrigatorio com Embalagem+Qtd preenchidas ([DA-636] p.3-4), entao a segunda
    // inscricao nascendo UN/1 travaria o Salvar de quem tem dois CNPJs. Edicao segue abrindo
    // vazia a linha que nao existe ([DA-636] p.2). Sem `prefill`: o "Vincular" do Monitor de Precos
    // abre sem preco (a coleta preenche depois, [DA-309]) e o UN/1 o faria recusar.
    const principalDefault = !item && !prefill
      ? (filiais.find((f) => f.principal) ?? filiais[0] ?? null)
      : null;
    setPernas(
      filiais.map((f) => {
        const existente = (origemTroca && principalNova?.id === f.id)
          ? { ...origemTroca, id: null, preco_atualizado_em: null, preco_origem: null }
          : (item?.pernas?.find((p) => p.filial_id === f.id) ?? null);
        const qtdPrefill = prefill?.quantidade_embalagem;
        const padrao = !existente && principalDefault?.id === f.id;
        return {
          filial_id: f.id,
          filial_nome: f.apelido || f.cidade || 'MATRIZ',
          uf: f.uf ?? null,
          perna_id: existente?.id ?? null,
          // Filial SEM oferta abre VAZIA. Ate 28/08 ela nascia com 'UN' e '1' — dois valores
          // validos —, entao a linha que nao existe aparecia preenchida e o operador nao tinha
          // como distinguir "nao gravou" de "gravou UN/1" ([DA-636]; e' a [DA-608] p.3 na forma
          // de DEFAULT em vez de placeholder). O vazio tambem e' o que deixa a trava de preco
          // abaixo valer sem barrar a filial que simplesmente nao vende o item.
          embalagem: existente?.embalagem || (padrao ? 'UN' : ''),
          quantidade_embalagem: existente?.quantidade_embalagem != null
            ? formatQtd(Number(existente.quantidade_embalagem))
            : (qtdPrefill != null && f.principal ? formatQtd(Number(qtdPrefill))
              : (padrao ? formatQtd(1) : '')),
          preco_unitario: existente?.preco_unitario != null
            ? formatMoedaSemSimbolo(Number(existente.preco_unitario), 5) : '',
          preco_atualizado_em: existente?.preco_atualizado_em ?? null,
          preco_origem: existente?.preco_origem ?? null,
        };
      }),
    );
  }, [filiais, item, prefill, empresaId]);

  useEffect(() => {
    api.get<{ id: number; nome: string }[]>('/api/marcas').then(setMarcas).catch(() => {});
    api.get<{ id: number; sigla: string; nome: string }[]>('/api/unidades-medida').then(setUnidades).catch(() => {});
  }, []);

  useEffect(() => {
    if (produtoFixo) return;
    if (buscaTimer.current) clearTimeout(buscaTimer.current);
    if (produtoBuscaText.length < 2 || produtoId || novoProdutoId) { setResultadosProduto([]); return; }
    buscaTimer.current = setTimeout(async () => {
      try {
        // ⛔ O PREFIXO `NP` decide qual prateleira responde, e as duas NUNCA aparecem juntas na
        // lista. Regra dele, 24/08/2026: *"Gostaria que a pesquisa iniciasse com NP..., para que
        // nao haja possibilidade de confundir os produtos"* — reafirmando o que ja tinha dito ao
        // desenhar o recurso (*"Quando digitar na pesquisa NP..., o sistema ja sabe que e um
        // produto de la"*). ⚠️ Eu tinha ampliado por conta propria pra buscar NP tambem por
        // descricao, com o argumento da [DA-468]; **este campo e' o que AMARRA a identidade do
        // item de catalogo**, e vincular no hub errado e' pior que nao achar de primeira.
        // A busca por descricao da prateleira continua existindo onde ela nao amarra nada: a tela
        // propria de Novos Produtos e as tres secoes de dropdown ([DA-606] p.5).
        const termo = produtoBuscaText.trim();
        const querPrateleiraNova = /^NP/i.test(termo);

        if (querPrateleiraNova) {
          const novos = await api.get<Array<{ id: number; codigo: string; descricao: string; marca: string | null; foto_url: string | null }>>(
            '/api/novos-produtos/busca', { query: { q: termo } },
          ).catch(() => []);
          setResultadosProduto((novos || []).slice(0, 15).map((n) => ({
            id: n.id,
            codigo_interno: n.codigo,
            descricao: n.descricao,
            foto_url: n.foto_url,
            marca_nome: n.marca,
            novo_produto: true,
          })));
          return;
        }

        const prods = await api.get<ProdutoBusca[]>('/api/produtos', { query: { q: termo, com_inativos: '1' } }).catch(() => []);
        setResultadosProduto((prods || []).slice(0, 15));
      } catch { setResultadosProduto([]); }
    }, 250);
    return () => { if (buscaTimer.current) clearTimeout(buscaTimer.current); };
  }, [produtoBuscaText, produtoFixo, produtoId, novoProdutoId]);

  function selecionarProduto(p: ProdutoBusca) {
    if (produtoInativo(p)) { void dialog.alerta(MSG_PRODUTO_INATIVO); return; }
    // Os dois hubs sao exclusivos: escolher um ZERA o outro. Deixar os dois preenchidos mandaria
    // ao servidor uma identidade dupla, e la o produto vence — o vinculo com a prateleira sumiria
    // sem aviso.
    if (p.novo_produto) { setNovoProdutoId(p.id); setProdutoId(null); }
    else { setProdutoId(p.id); setNovoProdutoId(null); }
    setProdutoBuscaText(`${p.codigo_interno} — ${p.descricao}`);
    setFotoUrl(p.foto_url);
    setResultadosProduto([]);
    // A prateleira nova ja traz descricao e marca digitadas por ele; completa o que estiver VAZIO
    // (nunca sobrescreve o que ele acabou de escrever).
    if (p.novo_produto) {
      setDescricaoFornecedor((d) => d.trim() || p.descricao);
      setMarca((m) => m.trim() || (p.marca_nome ?? ''));
    }
  }
  const tecladoProduto = useListaTeclado(resultadosProduto, selecionarProduto);

  async function iniciarTroca() {
    const ok = await dialog.confirmar(
      'Quer mesmo trocar o produto vinculado deste item do catalogo? Ele deixara de apontar para o codigo atual.',
      { titulo: 'Trocar produto vinculado', textoConfirmar: 'Trocar', perigoso: true },
    );
    if (!ok) return;
    setTrocandoProduto(true);
    setProdutoId(null);
    setNovoProdutoId(null);
    setProdutoBuscaText('');
    setFotoUrl(null);
  }

  function setPerna(filialId: number, campo: keyof PernaForm, valor: string) {
    setPernas((prev) => prev.map((p) => (p.filial_id === filialId ? { ...p, [campo]: valor } : p)));
  }

  function opcoesEmbalagem(emb: string) {
    // Inativa (migration 0311) so entra se for a que o item ja usa, marcada.
    const opts = unidades
      .filter((u) => u.ativo !== false || u.sigla === emb)
      .map((u) => ({ value: u.sigla, label: `${u.sigla} — ${u.nome}${u.ativo === false ? ' (inativa)' : ''}` }));
    if (emb && !unidades.some((u) => u.sigla === emb)) opts.unshift({ value: emb, label: `${emb} (nao cadastrada)` });
    return opts;
  }

  // O que se GRAVA e o codigo do fornecedor como ele o escreve (DA-471) — '0600300020' fica
  // '0600300020'. Quem casa e trava duplicata e o banco, pela coluna derivada.
  const codCru = limparCodigoFornecedor(codigoFornecedor);
  // Invariante do DA-309: sem produto E sem codigo o item nao tem identidade. Aqui a checagem e a
  // SIMPLES (tem texto?), nao a canonica: o caso que as duas separam e um codigo so de separador
  // ('---'), que o backend recusa com mensagem propria. Reimplementar a regra canonica no front pra
  // ganhar esse caso seria a 2a copia que a 0198 acabou de matar — e trava de tela nao pode ser
  // mais rigida que o backend (principio 5).
  // O item da prateleira nova conta como identidade pelo mesmo motivo do produto ([DA-610]): e um
  // registro nosso, estavel, que varios fornecedores podem apontar.
  const semIdentidade = !produtoId && !novoProdutoId && !codCru;

  // ---------------------------------------------------------------------------------------------
  // Codigo JA USADO neste fornecedor ([DA-635]). O upsert casa pelo codigo e ATUALIZA a linha que
  // ja existe — inclusive TROCANDO o produto vinculado, sem uma palavra: `produto_id = COALESCE(
  // <novo>, produto_id)` so protege contra vazio, valor novo SUBSTITUI. E' a [DA-617] outra vez —
  // dois caminhos, mesmo destino, vence quem grava por ultimo. Quem cadastra "um item novo" com um
  // codigo que ja e' de outro produto nao tem como saber: a tela respondia "Item cadastrado no
  // catalogo" e o vinculo certo tinha ido embora.
  // A peca que responde ja existia — o `GET /por-codigo` do de-para da nota. Nada de rota nova.
  // ---------------------------------------------------------------------------------------------
  const [itemMesmoCodigo, setItemMesmoCodigo] = useState<ItemMesmoCodigo | null>(null);
  const reqCodigo = useRef(0);

  useEffect(() => {
    if (!empresaId || !codCru) { setItemMesmoCodigo(null); return; }
    // Guarda de corrida: o campo e' debounced e a empresa muda na hora — sem isto a resposta de um
    // codigo antigo pode chegar depois e mandar na tela.
    const req = ++reqCodigo.current;
    const t = setTimeout(() => {
      api.get<{ encontrado: boolean; item: ItemMesmoCodigo | null }>(
        '/api/fornecedor-catalogo/por-codigo',
        { query: { fornecedor_id: Number(empresaId), codigo: codCru } },
      )
        .then((r) => {
          if (req !== reqCodigo.current) return;
          // O item que EU estou editando nao e' "outro": o aviso e' sobre pisar em linha alheia.
          setItemMesmoCodigo(r.encontrado && r.item && r.item.item_id !== (item?.id ?? -1) ? r.item : null);
        })
        .catch(() => { if (req === reqCodigo.current) setItemMesmoCodigo(null); });
    }, 400);
    return () => clearTimeout(t);
  }, [empresaId, codCru, item?.id]);

  // So e' TROCA quando a linha que ja existe aponta pra um produto e o escolhido aqui e' OUTRO.
  // Item existente sem produto nenhum apenas ganha o vinculo — isso completa, nao substitui.
  const trocaDeVinculo = !!(
    itemMesmoCodigo?.produto_id && produtoId && itemMesmoCodigo.produto_id !== produtoId
  );

  async function salvar() {
    if (!empresaId) { toast.erro('Escolha o fornecedor'); return; }
    // Na troca de fornecedor o item MUDA de empresa; ele nao se funde com o que la ja tem o codigo.
    // O servidor recusaria pelo unique — aqui a recusa sai antes, dizendo qual e' o outro.
    if (trocandoFornecedor && itemMesmoCodigo) {
      toast.erro(
        `O fornecedor escolhido ja tem um item com o codigo ${itemMesmoCodigo.codigo_fornecedor || codCru}. `
        + 'Apague este item aqui e, se precisar, edite o que ja existe la.',
      );
      return;
    }
    if (semIdentidade) {
      toast.erro('Informe o produto do cadastro (ou um item de Novos Produtos) OU o codigo do fornecedor — sem um dos dois o item nao tem identidade.');
      return;
    }
    // A faixa la em cima ja avisa; esta e' a trava. O rotulo do botao diz "Salvar" e o efeito e'
    // trocar o dono de um codigo — a consequencia precisa ser NOMEADA antes ([DA-434]).
    if (trocaDeVinculo && itemMesmoCodigo) {
      const ok = await dialog.confirmar(
        `O codigo ${itemMesmoCodigo.codigo_fornecedor || codCru} deste fornecedor hoje aponta para o produto `
        + `${itemMesmoCodigo.produto_codigo} — ${itemMesmoCodigo.produto_descricao}.\n\n`
        + `Salvar vai TROCAR esse vinculo para o produto que voce escolheu. A partir dai, toda nota `
        + `de entrada e toda cotacao com esse codigo cai no produto novo.\n\nConfirma a troca?`,
        { perigoso: true },
      );
      if (!ok) return;
    }
    // Embalagem + Qtd preenchidas exigem PRECO (regra dele, 28/08 — [DA-636]). Ate aqui a perna
    // NOVA sem preco era descartada calada logo abaixo, junto com a embalagem e a quantidade que
    // ele acabara de digitar: o Salvar respondia "Item atualizado" e os dois campos nunca saiam
    // do navegador. A saida vai NOMEADA na recusa ([DA-560]) — sem preco, apagar os dois grava so
    // a identidade, que e' o caso da compra informativa da [DA-309].
    const numeros = (p: PernaForm) => ({
      embalagem: p.embalagem,
      quantidade_embalagem: parseQtd(p.quantidade_embalagem),
      preco_unitario: parseMoeda(p.preco_unitario),
    });
    const semPreco = pernas.find((p) => pernaSemPrecoObrigatorio(numeros(p)));
    if (semPreco) {
      toast.erro(
        `${semPreco.filial_nome}: informe o Preco da emb. — com Embalagem e Qtd na emb. preenchidas o preco e obrigatorio. `
        + 'Se ainda nao sabe o preco, apague a Embalagem e a Qtd para gravar so a identidade do item.',
      );
      return;
    }
    // So sobe perna que ja existe OU em que ele mexeu em ALGUMA coisa. O filtro antigo olhava so o
    // preco, e por isso comia embalagem e quantidade digitadas ([DA-636]).
    const pernasPayload = pernas
      .filter((p) => p.perna_id != null || pernaTemConteudo(numeros(p)))
      .map((p) => ({
        filial_id: p.filial_id,
        embalagem: p.embalagem || null,
        quantidade_embalagem: parseQtd(p.quantidade_embalagem) || null,
        preco_unitario: parseMoeda(p.preco_unitario) || null,
      }));
    // Nenhuma perna com preco: o backend grava o item na inscricao principal, sem preco — e o caso
    // da compra informativa, que registra o SKU antes de saber quanto ele custa.

    try {
      setSalvando(true);
      await api.put('/api/fornecedor-catalogo/upsert', {
        item_id: item?.id ?? null,
        fornecedor_id: Number(empresaId),
        produto_id: produtoId,
        novo_produto_id: novoProdutoId,
        codigo_fornecedor: codCru || null,
        codigo_fornecedor_alt: codigoAlt.trim() || null,
        descricao_fornecedor: descricaoFornecedor || null,
        marca: marca || null,
        ncm: ncm || null,
        pernas: pernasPayload,
        // Na EDICAO cada linha abriu com o gravado e volta com o estado final: o `×` da Embalagem
        // (ou apagar Qtd/Preco) apaga no banco — sem isto o servidor lia vazio como "manter" e o UN
        // voltava ao reabrir. Item NOVO fica fora: o codigo pode cair numa linha que ja existe e
        // que esta tela nunca carregou, e ali vazio e' "nao sei" (o caso da [DA-635]).
        pernas_inteiras: item != null,
      });
      if (!item) {
        try { localStorage.setItem(CHAVE_ULTIMO_FORNECEDOR, String(Number(empresaId))); } catch { /* sem storage, abre vazio */ }
      }
      toast.sucesso(item ? 'Item atualizado.' : 'Item cadastrado no catalogo.');
      onSalvo();
    } catch (e: unknown) {
      toast.erro(e instanceof ApiError ? e.message : 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  // Ate onde os campos vao no grid de 12 colunas: com foto, as duas ultimas sao dela.
  // Sem foto os campos ocupam a largura toda — reservar a coluna sempre deixaria um vao a direita.
  const fimCampos = fotoUrl ? 11 : 13;

  const label: CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, marginBottom: 4 };
  const input: CSSProperties = {
    width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 6,
    fontSize: 13, background: 'var(--input-bg)', color: 'var(--text-color)',
  };

  return (
    <Modal
      aberto
      onFechar={onFechar}
      titulo={item ? 'Editar item do catalogo' : 'Novo item no catalogo'}
      largura={1180}
      rodape={
        <>
          <button type="button" className="btn-secundario" onClick={onFechar} disabled={salvando}>Cancelar</button>
          <button type="button" className="btn-primario" onClick={() => void salvar()} disabled={salvando}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <Save size={14} /> {salvando ? 'Salvando...' : 'Salvar'}
          </button>
        </>
      }
    >
      {/* Codigo ja usado neste fornecedor ([DA-635]). Fica FORA da grade de identidade de proposito:
          ela tem tres linhas cravadas e a foto atravessa as tres (`gridRow: '1 / 4'`) — campo novo
          la dentro cairia numa 4a linha que a foto nao cobre. Cor SOLIDA com letra preta, que e' a
          regra da casa pra aviso; laranja porque isto e' atencao, nao erro. */}
      {itemMesmoCodigo && (
        <div style={{ background: '#f97316', color: '#000', borderRadius: 6, padding: '10px 12px',
                      marginBottom: 12, fontSize: 13, lineHeight: 1.45 }}>
          <strong>Este fornecedor ja tem um item com o codigo {itemMesmoCodigo.codigo_fornecedor || codCru}.</strong>
          {trocandoFornecedor ? (
            <> Este item nao pode ser movido pra ele — os dois ficariam com o mesmo codigo
              {itemMesmoCodigo.produto_codigo && (
                <> (o de la aponta pro produto <strong>{itemMesmoCodigo.produto_codigo} — {itemMesmoCodigo.produto_descricao}</strong>)</>
              )}.
            </>
          ) : (
            <>
              {' '}Salvar vai <strong>atualizar o item que ja existe</strong>, nao criar um novo
              {itemMesmoCodigo.produto_codigo && (
                <> — hoje ele aponta pro produto <strong>{itemMesmoCodigo.produto_codigo} — {itemMesmoCodigo.produto_descricao}</strong></>
              )}.
            </>
          )}
          {trocaDeVinculo && !trocandoFornecedor && (
            <div style={{ marginTop: 6, fontWeight: 700 }}>
              Salvar assim TROCA esse vinculo pelo produto escolhido acima. Confira se o codigo e'
              mesmo deste produto — dai pra frente, nota e cotacao com ele caem no produto novo.
            </div>
          )}
        </div>
      )}

      {/* ---------- Identidade: e da EMPRESA, nao se repete por filial ----------
          Sao TRES LINHAS de campos, e cada um declara `gridRow` — nao e' zelo: a foto atravessa as
          tres (`gridRow: '1 / 4'`) e auto-placement nao daria pra ela onde parar. ⚠️ Campo NOVO aqui
          declara a sua linha, senao ele cai numa 4a que a foto nao cobre.
          Antes eram sete campos EMPILHADOS num modal de 860px, com a foto de 112px mandando na
          altura da 1a linha (o campo Fornecedor pede 79px): na janela dele — 1536x740 CSS, que e' o
          print de 1920 a 125% — isso escondia **139px** atras da rolagem. Medido na tela publicada:
          724px no teto com 139 escondidos -> 637px com tudo visivel. A foto virou COLUNA propria a
          direita, receita da [DA-609] p.6, e o teto continua em PIXEL — o `tam` do FotoProduto vira
          `width` com `aspectRatio: 1`, e percentual nao segura moldura de altura `auto`. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 12, marginBottom: 12, alignItems: 'start' }}>
        <div style={{ gridColumn: '1 / 6', gridRow: 1 }}>
          <div style={{ minWidth: 0 }}>
            <label style={label}>Fornecedor</label>
            <ComboBox
              value={empresaId === '' ? null : Number(empresaId)}
              onChange={(v) => setEmpresaId(v === '' || v == null ? '' : Number(v))}
              opcoes={fornecedores.map((f) => ({ value: f.id, label: f.nome_fantasia || `Fornecedor ${f.id}` }))}
              placeholder="Escolha o fornecedor..."
              disabled={!podeTrocarFornecedor}
            />
            {/* Travado diz por que (botao desabilitado DIZ por que); trocando, diz o que acontece. */}
            {item && !podeTrocarFornecedor && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                {item.importacoes == null
                  ? 'Fornecedor travado: abra o item pela tela do Catalogo para poder trocar.'
                  : `Fornecedor travado: o item ja tem ${contar(item.importacoes, 'importacao de XML', 'importacoes de XML')}.`}
              </div>
            )}
            {trocandoFornecedor && (
              <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>
                O item sai de {item?.empresa_nome || 'fornecedor anterior'} e passa para este ao salvar.
                O preco digitado veio junto — confira la embaixo.
                {vinculadoAoMonitor && ' O vinculo com o Monitor de Precos do fornecedor anterior sai.'}
              </div>
            )}
            {filiais.length > 1 && (
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Esta empresa tem {filiais.length} CNPJs — o item vale pra todos, e cada um tem o seu
                preco la embaixo.
              </div>
            )}
          </div>
        </div>

        {/* `alignSelf: 'start'` porque item de grid ESTICA por padrao (`stretch`): esticada, ela
            voltaria a decidir a altura da faixa — que e' o defeito que esta arrumacao desfez. */}
        {fotoUrl && (
          <FotoProduto
            url={fotoUrl}
            tam={150}
            style={{ borderRadius: 8, gridColumn: '11 / 13', gridRow: '1 / 4', alignSelf: 'start', justifySelf: 'center' }}
          />
        )}

        <div style={{ gridColumn: `6 / ${fimCampos}`, gridRow: 1, position: 'relative' }}>
          <label style={label}>
            Produto interno
            {produtoFixo && item && (
              <button type="button" onClick={() => void iniciarTroca()}
                      style={{ marginLeft: 8, fontSize: 11, fontWeight: 400, background: 'none', border: 'none', color: '#0ea5e9', cursor: 'pointer', textDecoration: 'underline' }}>
                trocar produto vinculado
              </button>
            )}
          </label>
          {produtoFixo ? (
            <div style={{ ...input, background: 'var(--card-bg)', color: 'var(--text-muted)' }}>{produtoBuscaText || '—'}</div>
          ) : (
            <>
              <input
                type="text"
                value={produtoBuscaText}
                onChange={(e) => { setProdutoBuscaText(e.target.value); setProdutoId(null); setNovoProdutoId(null); setFotoUrl(null); }}
                onKeyDown={tecladoProduto.onKeyDown}
                // O prefixo NP e' o unico caminho pra prateleira nova neste campo, entao ele vai
                // ESCRITO aqui — recurso que nao aparece na tela nao existe (principio 1). E' uma
                // instrucao, nao um exemplo copiavel: as reticencias impedem que ele leia como um
                // valor ja preenchido ([DA-608] p.3).
                placeholder="Busque por codigo ou descricao — ou digite NP... para achar em Novos Produtos"
                style={input}
              />
              {resultadosProduto.length > 0 && (
                <div style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 30,
                  background: 'var(--panel-bg)', border: '1px solid var(--border)', borderRadius: 6,
                  maxHeight: 200, overflowY: 'auto',
                }}>
                  {resultadosProduto.map((p, i) => (
                    <div
                      // As duas prateleiras dividem a lista e os ids se repetem entre elas (o
                      // produto 9 e o NP 9 existem os dois): a chave leva a ORIGEM junto, senao o
                      // React reusa a linha errada quando a busca muda.
                      key={`${p.novo_produto ? 'np' : 'p'}-${p.id}`}
                      onClick={() => selecionarProduto(p)}
                      style={{
                        padding: '6px 10px', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
                        background: i === tecladoProduto.highlight ? 'var(--grid-row-alt-bg, #f3f4f6)' : undefined,
                      }}
                    >
                      {p.foto_url
                        ? <FotoProduto url={p.foto_url} tam={28} style={{ flexShrink: 0 }} />
                        : <div style={{ width: 28, height: 28, borderRadius: 3, background: 'var(--grid-row-alt-bg, #f3f4f6)', flexShrink: 0 }} />}
                      <span>
                        {/* Selo da prateleira nova — o MESMO lilas das secoes do dropdown da
                            cotacao ([DA-606] p.10). Cor propria porque as duas origens aparecem
                            juntas na lista e a mesma cor as faria ler como a mesma coisa. */}
                        {p.novo_produto && (
                          <span style={{ background: '#c4b5fd', color: '#000', borderRadius: 3, padding: '1px 5px', fontSize: 10, fontWeight: 700, marginRight: 6 }}>
                            NOVO
                          </span>
                        )}
                        {produtoInativo(p) && <SeloInativo />}
                        <strong>{p.codigo_interno}</strong> — {p.descricao}{p.marca_nome ? <span style={{ color: 'var(--primary)', marginLeft: 6 }}>[{p.marca_nome}]</span> : null}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>

        <div style={{ gridColumn: '1 / 4', gridRow: 2 }}>
          <label style={label}>Codigo do fornecedor</label>
          <input type="text" value={codigoFornecedor} onChange={(e) => setCodigoFornecedor(upperMantendoCursor(e))} style={input} />
        </div>
        <div style={{ gridColumn: '4 / 6', gridRow: 2 }}>
          <label style={label}>NCM</label>
          <input type="text" value={ncm} onChange={(e) => setNcm(e.target.value.replace(/\D/g, '').slice(0, 8))} style={input} />
        </div>

        <div style={{ gridColumn: `6 / ${fimCampos}`, gridRow: 2 }}>
          <label style={label}>Codigo alternativo (o que vem na NF-e)</label>
          <input
            type="text"
            value={codigoAlt}
            onChange={(e) => setCodigoAlt(upperMantendoCursor(e))}
            style={input}
            placeholder="deixe vazio se o fornecedor usa um codigo so"
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.4 }}>
            Alguns fornecedores usam <b>dois codigos pro mesmo item</b>: um na tabela de precos (o de
            cima, que sai no pedido de compra) e outro na nota fiscal. Preenchendo aqui o da nota, a
            importacao do XML <b>atualiza esta linha</b> em vez de criar outra — e o preco entra
            convertido pra embalagem que esta cadastrada aqui.
          </div>
        </div>

        <div style={{ gridColumn: '1 / 8', gridRow: 3 }}>
          <label style={label}>Descricao do fornecedor</label>
          <input type="text" value={descricaoFornecedor} onChange={(e) => setDescricaoFornecedor(upperMantendoCursor(e))} style={input} />
        </div>

        <div style={{ gridColumn: `8 / ${fimCampos}`, gridRow: 3 }}>
          <label style={label}>Marca</label>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <div style={{ flex: 1 }}>
              <ComboBox
                value={marca || null}
                onChange={(v) => setMarca(v === '' || v == null ? '' : String(v))}
                opcoes={marcas.map((m) => ({ value: m.nome, label: m.nome }))}
                placeholder="Escolha a marca..."
              />
            </div>
            <button type="button" className="btn-secundario" onClick={() => setMostrarNovaMarca(true)}
                    style={{ padding: '6px 10px', fontSize: 12, whiteSpace: 'nowrap' }} title="Cadastrar uma marca nova">
              <Plus size={12} /> Marca
            </button>
          </div>
        </div>
      </div>

      {semIdentidade && (
        <div style={{ background: '#f97316', color: '#000', padding: 8, borderRadius: 6, fontSize: 12, fontWeight: 500, marginBottom: 12 }}>
          Informe o produto do cadastro OU o codigo do fornecedor — sem um dos dois o item nao tem identidade.
        </div>
      )}

      {/* ---------- Precos: um por FILIAL. Cada perna leva a SUA quantidade. ---------- */}
      {pernas.length > 0 && (
        <div style={{ borderTop: '1px solid var(--border)', paddingTop: 12 }}>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Preco por CNPJ</div>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
            Cada inscricao tem a sua embalagem e a sua quantidade — uma pode faturar o metro e a
            outra o rolo. Sem preco, nao vira oferta.
          </div>

          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: 'var(--text-muted)', fontSize: 11 }}>
                <th style={{ textAlign: 'left', padding: '4px 6px' }}>CNPJ</th>
                <th style={{ textAlign: 'left', padding: '4px 6px', width: 150 }}>Embalagem</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', width: 80 }}>Qtd na emb.</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', width: 140 }}>Preco da emb. (R$)</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', width: 100 }}>Unitario</th>
                <th style={{ textAlign: 'right', padding: '4px 6px', width: 170 }} title="Data e hora da ultima atualizacao deste preco (so-leitura)">Atualizado em</th>
              </tr>
            </thead>
            <tbody>
              {pernas.map((p) => {
                const qtd = parseQtd(p.quantidade_embalagem);
                const preco = parseMoeda(p.preco_unitario);
                const unit = qtd > 0 && preco > 0 ? preco / qtd : null;
                const daCasa = String(p.uf || '').toUpperCase() === UF_CASA;
                return (
                  <tr key={p.filial_id} style={{ borderTop: '1px solid var(--border)' }}>
                    <td style={{ padding: '6px', fontWeight: 600 }}>
                      {p.filial_nome}
                      <span style={{ display: 'block', fontSize: 10, fontWeight: 400, color: 'var(--text-muted)' }}>
                        {daCasa ? 'no estado' : `${p.uf || '?'} · sofre DIFAL`}
                      </span>
                    </td>
                    <td style={{ padding: '6px' }}>
                      <ComboBox
                        value={p.embalagem || null}
                        onChange={(v) => setPerna(p.filial_id, 'embalagem', v === '' || v == null ? '' : String(v))}
                        opcoes={opcoesEmbalagem(p.embalagem)}
                        // "UN" e' uma embalagem VALIDA desta lista, entao como placeholder ele
                        // fazia celula vazia parecer preenchida ([DA-608] p.3, medido na
                        // prateleira irma). Placeholder e' instrucao, nunca um valor possivel.
                        placeholder="Escolha..."
                      />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <input
                        type="text"
                        value={p.quantidade_embalagem}
                        onChange={(e) => setPerna(p.filial_id, 'quantidade_embalagem', e.target.value)}
                        onBlur={(e) => setPerna(p.filial_id, 'quantidade_embalagem', formatQtd(parseQtd(e.target.value)))}
                        style={{ ...input, textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '6px' }}>
                      <input
                        type="text"
                        value={p.preco_unitario}
                        onChange={(e) => setPerna(p.filial_id, 'preco_unitario', e.target.value)}
                        onBlur={(e) => {
                          const v = parseMoeda(e.target.value);
                          setPerna(p.filial_id, 'preco_unitario', v > 0 ? formatMoedaSemSimbolo(v, 5) : '');
                        }}
                        placeholder="—"
                        style={{ ...input, textAlign: 'right' }}
                      />
                    </td>
                    <td style={{ padding: '6px' }}>
                      {/* So-leitura (calculado): moldura de input, fundo branco e fonte azul-marinho do
                          tema — mesmo visual dos editaveis, so o cursor bloqueado marca que nao edita. */}
                      <div
                        style={{ ...input, color: 'var(--primary)', cursor: 'not-allowed', textAlign: 'right', whiteSpace: 'nowrap' }}
                        title="Preco unitario (calculado: preco da embalagem / quantidade) — so-leitura"
                      >
                        {unit != null ? `${formatMoedaSemSimbolo(unit, 4)}/un` : '—'}
                      </div>
                    </td>
                    <td style={{ padding: '6px' }}>
                      {/* So-leitura: moldura de input, fundo branco e fonte azul-marinho do tema (--input-bg /
                          --text-color). Data/hora da ultima atualizacao de preco. */}
                      <div
                        style={{
                          ...input,
                          color: 'var(--primary)',
                          cursor: 'not-allowed',
                          textAlign: 'right',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                        title={p.preco_atualizado_em
                          ? 'Data e hora da ultima atualizacao deste preco (so-leitura)'
                          : 'Sem registro — preco anterior ao rastreio de atualizacao, ou perna ainda sem preco'}
                      >
                        {p.preco_atualizado_em ? (
                          <>
                            {formatDataSP(p.preco_atualizado_em)} {formatHoraSP(p.preco_atualizado_em)}
                            {p.preco_origem && PRECO_ORIGEM_SELO[p.preco_origem] && (
                              <span> · {PRECO_ORIGEM_SELO[p.preco_origem]!.texto}</span>
                            )}
                          </>
                        ) : '—'}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {mostrarNovaMarca && (
        <ModalCadastroRapido
          titulo="Nova Marca"
          endpoint="/api/marcas"
          campos={[{ key: 'nome', label: 'Nome', obrigatorio: true }]}
          onFechar={() => setMostrarNovaMarca(false)}
          onSalvo={(novo: { id: number; nome: string }) => {
            setMarcas((prev) => [...prev, novo].sort((a, b) => a.nome.localeCompare(b.nome)));
            setMarca(novo.nome);
            setMostrarNovaMarca(false);
          }}
        />
      )}
    </Modal>
  );
}
